// OverflowMenu — tombol titik-tiga (kebab) + dropdown aksi sekunder. Untuk
// merapikan toolbar: aksi penting tetap tampil, sisanya masuk sini. Ringan
// (tanpa dep dropdown eksternal): tutup saat klik di luar / Escape / pilih.

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { MoreHorizontal } from "lucide-react";
import { cn } from "@ds/lib/utils";
import { Button } from "./Button";

export interface OverflowMenuItem {
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  /** Sorot/peringatan (mis. aksi destruktif). */
  danger?: boolean;
}

export interface OverflowMenuProps {
  items: OverflowMenuItem[];
  /** Label aksesibilitas tombol pemicu. */
  label?: string;
  align?: "start" | "end";
}

export function OverflowMenu({ items, label = "Menu lainnya", align = "end" }: OverflowMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    function onDown(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!items.length) return null;

  return (
    <div ref={rootRef} className="relative">
      <Button
        size="icon"
        variant="ghost"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        <MoreHorizontal className="size-4" aria-hidden />
      </Button>
      {open ? (
        <div
          id={menuId}
          role="menu"
          className={cn(
            "z-dropdown absolute top-full mt-1 min-w-48 overflow-hidden rounded-lg border border-border bg-surface py-1 shadow-[var(--shadow-pop)] pop-in",
            align === "end" ? "right-0" : "left-0",
          )}
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors",
                "hover:bg-faint disabled:cursor-not-allowed disabled:opacity-50",
                item.danger ? "text-danger" : "text-ink",
              )}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
            >
              {item.icon ? <span className="shrink-0 text-muted">{item.icon}</span> : null}
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
